<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import TelegramUserAPI from 'dashboard/api/telegramUser';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const STEP_PHONE = 'phone';
const STEP_CODE = 'code';
const STEP_PASSWORD = 'password';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      step: STEP_PHONE,
      phoneNumber: '',
      code: '',
      password: '',
      // Restituito dal sidecar: identifica la connessione che ha generato il
      // phone_code_hash e va rimandato indietro a ogni passo successivo.
      loginId: '',
      isSubmitting: false,
      syncSettings: { private: true, group: true, channel: true },
    };
  },
  computed: {
    isPhoneStep() {
      return this.step === STEP_PHONE;
    },
    isCodeStep() {
      return this.step === STEP_CODE;
    },
    isPasswordStep() {
      return this.step === STEP_PASSWORD;
    },
    // Almeno un tipo di chat deve restare attivo, altrimenti l'inbox non riceverebbe nulla.
    hasPeerSelection() {
      return Object.values(this.syncSettings).some(Boolean);
    },
  },
  validations: {
    phoneNumber: { required, minLength: minLength(8) },
    code: { required },
    password: { required },
  },
  methods: {
    handleError(error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        this.$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.API.ERROR_MESSAGE');
      useAlert(message);

      // 410 dal sidecar: il login e' scaduto o il codice non e' piu' valido.
      // Non ha senso insistere sul passo corrente, si riparte dal numero.
      if (error?.response?.status === 410) {
        this.step = STEP_PHONE;
        this.loginId = '';
        this.code = '';
        this.password = '';
      }
    },

    async requestCode() {
      this.v$.phoneNumber.$touch();
      if (this.v$.phoneNumber.$invalid) return;
      if (!this.hasPeerSelection) {
        useAlert(this.$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SYNC.REQUIRED'));
        return;
      }

      this.isSubmitting = true;
      try {
        const { data } = await TelegramUserAPI.requestCode(this.phoneNumber);
        this.loginId = data.login_id;
        this.step = STEP_CODE;
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    async verifyCode() {
      this.v$.code.$touch();
      if (this.v$.code.$invalid) return;

      this.isSubmitting = true;
      try {
        const { data } = await TelegramUserAPI.verifyCode({
          loginId: this.loginId,
          code: this.code,
          syncSettings: this.syncSettings,
        });

        if (data.status === 'needs_password') {
          this.step = STEP_PASSWORD;
          return;
        }
        this.goToAgents(data);
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    async verifyPassword() {
      this.v$.password.$touch();
      if (this.v$.password.$invalid) return;

      this.isSubmitting = true;
      try {
        const { data } = await TelegramUserAPI.verifyPassword({
          loginId: this.loginId,
          password: this.password,
          syncSettings: this.syncSettings,
        });
        this.goToAgents(data);
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
      }
    },

    goToAgents(data) {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: data.id },
      });
    },

    restart() {
      this.step = STEP_PHONE;
      this.loginId = '';
      this.code = '';
      this.password = '';
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.DESC')"
    />

    <div
      class="p-3 mb-4 text-sm rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200"
    >
      {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.WARNING') }}
    </div>

    <!-- passo 1: numero -->
    <form
      v-if="isPhoneStep"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="requestCode()"
    >
      <div class="flex-shrink-0 flex-grow-0 w-full">
        <label :class="{ error: v$.phoneNumber.$error }">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE.LABEL') }}
          <input
            v-model="phoneNumber"
            type="tel"
            :placeholder="
              $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE.PLACEHOLDER')
            "
            @blur="v$.phoneNumber.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-2 mb-4">
        <label class="font-medium text-sm">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SYNC.LABEL') }}
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SYNC.SUBTITLE') }}
        </p>
        <div class="flex flex-col gap-2 mt-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input v-model="syncSettings.private" type="checkbox" />
            <span>{{
              $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SYNC.PRIVATE')
            }}</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input v-model="syncSettings.group" type="checkbox" />
            <span>{{
              $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SYNC.GROUP')
            }}</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input v-model="syncSettings.channel" type="checkbox" />
            <span>{{
              $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SYNC.CHANNEL')
            }}</span>
          </label>
        </div>
      </div>

      <div class="w-full">
        <NextButton
          :is-loading="isSubmitting"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE.SUBMIT')"
        />
      </div>
    </form>

    <!-- passo 2: codice -->
    <form
      v-else-if="isCodeStep"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="verifyCode()"
    >
      <div class="flex-shrink-0 flex-grow-0 w-full">
        <label :class="{ error: v$.code.$error }">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.LABEL') }}
          <input
            v-model="code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            :placeholder="
              $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.PLACEHOLDER')
            "
            @blur="v$.code.$touch"
          />
        </label>
        <p class="help-text">
          {{
            $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.SUBTITLE', {
              phone: phoneNumber,
            })
          }}
        </p>
      </div>

      <div class="w-full mt-4 flex gap-2">
        <NextButton
          :is-loading="isSubmitting"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CODE.SUBMIT')"
        />
        <NextButton
          faded
          slate
          type="button"
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RESTART')"
          @click="restart"
        />
      </div>
    </form>

    <!-- passo 3: 2FA -->
    <form
      v-else-if="isPasswordStep"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="verifyPassword()"
    >
      <div class="flex-shrink-0 flex-grow-0 w-full">
        <label :class="{ error: v$.password.$error }">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.LABEL') }}
          <input
            v-model="password"
            type="password"
            autocomplete="current-password"
            :placeholder="
              $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.PLACEHOLDER')
            "
            @blur="v$.password.$touch"
          />
        </label>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4 flex gap-2">
        <NextButton
          :is-loading="isSubmitting"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PASSWORD.SUBMIT')"
        />
        <NextButton
          faded
          slate
          type="button"
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RESTART')"
          @click="restart"
        />
      </div>
    </form>
  </div>
</template>
